import { eachDay, isSameDay } from 'date-fns'
import { flatten, sortedIndexBy, sortedLastIndexBy } from 'lodash'
import { IEvent } from '../../models/Event'
import { FeedingType } from '../../models/Object'
import { IRoomWithEvents } from '../../reducers/bookingCalendar/selectors/rooms'

export function getBoarding(event: IEvent, date: Date): number {
  const calc = {
    [FeedingType.WithoutFood]: 0,
    [FeedingType.Breakfast]: 1,
    [FeedingType.WithDinner]: 2,
    [FeedingType.FullFood]: 3,
  }

  if (event.service === FeedingType.WithoutFood) {
    return 0
  }

  const persons = event.kids + event.adults

  const serviceNumber = calc[event.service] || 0

  if (+event.startDate === +date) {
    return persons * (serviceNumber && serviceNumber - 1)
  }
  if (+event.finishDate === +date) {
    return persons
  }

  return persons * serviceNumber
}

export function getCheckIn(events: IEvent[], date: Date) {
  return events.filter(event => isSameDay(event.startDate, date)).length
}

export function getCheckOuts(events: IEvent[], date: Date) {
  return events.filter(event => isSameDay(event.finishDate, date)).length
}

const getEmptyDaySummary = () => ({
  boarding: 0,
  checkIns: 0,
  checkOuts: 0,
  persons: 0,
})

export function getPersons(events: IEvent[]) {
  return events
    .map(event => event.adults + event.kids)
    .reduce((a, b) => a + b, 0)
}

export default function reduceForSummary(
  rooms: Pick<IRoomWithEvents, 'events'>[],
  from: Date,
  to: Date,
): ISummaryResults {
  const events = flatten(
    rooms.map(room => {
      const start = sortedIndexBy<{ startDate: Date }>(
        room.events,
        { startDate: from },
        e => +e.startDate,
      )

      const end = sortedLastIndexBy<{ finishDate: Date }>(
        room.events,
        { finishDate: to },
        e => +e.finishDate,
      )

      return room.events.slice(start, end)
    }),
  )

  return events.reduce((acc, event) => {
    eachDay(event.startDate, event.finishDate).forEach(v => {
      if (!acc[+v]) {
        acc[+v] = getEmptyDaySummary()
      }
      acc[+v].persons += event.adults + event.kids
      acc[+v].boarding += getBoarding(event, v)
    })

    acc[+event.startDate].checkIns += 1
    acc[+event.finishDate].checkOuts += 1

    return acc
  }, {})
}

export interface ISummaryResult {
  boarding: number
  checkIns: number
  checkOuts: number
  persons: number
}

export interface ISummaryResults {
  [key: number]: ISummaryResult
}
